import { useCallback, useContext, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Box } from '@mui/material';
import BookingDetailsModal from '../Bookings/BookingDetailsModal/BookingDetailsModal';
import CreateBookingModal from '../Bookings/CreateBookingsModal/CreateBookingModal';
import { BookingProvider } from '../../context/bookingContext';
import { viewRootTextPrimarySx } from '../../util/chartTheme';
import EditableList from '../EditableList/EditableList';
import AuthContext from '../../context/authContext';

const DEFAULT_VISIBLE_KEYS = [
  'firstName',
  'lastName',
  'bookingNumber',
  'mail',
  'phone',
  'startTime',
  'entertainment',
  'participantCount',
  'branchLabel',
  'creatorLabel',
  'status',
];

const BOOKING_SELECT_KEYS = [
  'firstName',
  'lastName',
  'bookingNumber',
  'mail',
  'phone',
  'startTime',
  'endTime',
  'eventDurationMS',
  'status',
  'entertainment',
  'entertainmentId',
  'participantCount',
  'fullyPaid',
  'branchId',
  'selectedPackage',
  'presentSince',
  'experienceRating',
  'finishedAt',
  'meta',
];

function statusLabel(val) {
  if (val === 'confirmed') return 'Bestätigt';
  if (val === 'canceled') return 'Storniert';
  if (val === 'noShow') return 'Nicht erschienen';
  if (val === 'awaitingConfirmation') return 'Wartet auf Bestätigung';
  if (val === 'rejected') return 'Abgelehnt';
  if (val === 'waitingForPayment') return 'Wartet auf Zahlung';
  if (val === 'blocked') return 'Blockiert';
  return val || '';
}

function experienceLabel(val) {
  if (val === 'good') return 'Gut';
  if (val === 'neutral') return 'Neutral';
  if (val === 'bad') return 'Schlecht';
  return '';
}

function BookingSearchView() {
  const location = useLocation();
  const authCtx = useContext(AuthContext);
  const [initialSearchSeed] = useState(() => location.state?.initialSearch ?? '');

  const [selectedBookingId, setSelectedBookingId] = useState(null);
  const [createModalOpen, setCreateModalOpen] = useState(false);

  const currentUserId = authCtx.currentUser?.userId;

  const statusOptions = useMemo(
    () => [
      { value: 'confirmed', label: 'Bestätigt' },
      { value: 'canceled', label: 'Storniert' },
      { value: 'noShow', label: 'Nicht erschienen' },
      { value: 'awaitingConfirmation', label: 'Wartet auf Bestätigung' },
      { value: 'rejected', label: 'Abgelehnt' },
      { value: 'waitingForPayment', label: 'Wartet auf Zahlung' },
      { value: 'blocked', label: 'Blockiert' },
    ],
    []
  );

  const creatorOptions = useMemo(
    () => [
      { value: 'mine', label: 'Ich' },
      { value: 'other', label: 'Anderer Nutzer' },
    ],
    []
  );

  const bookingSchema = useMemo(
    () => ({
      firstName: { label: 'Vorname', type: 'text', readOnly: true, minWidth: '120px' },
      lastName: { label: 'Nachname', type: 'text', readOnly: true, minWidth: '120px' },
      bookingNumber: { label: 'Buchungsnummer', type: 'text', readOnly: true, minWidth: '130px' },
      mail: { label: 'Mail', type: 'text', readOnly: true, minWidth: '200px' },
      phone: { label: 'Telefon', type: 'text', readOnly: true, minWidth: '120px' },
      startTime: {
        label: 'Startzeit',
        type: 'date',
        readOnly: true,
        minWidth: '150px',
        sortType: 'date',
      },
      endTime: { label: 'Endzeit', type: 'date', readOnly: true, minWidth: '150px' },
      eventDurationMin: {
        label: 'Dauer (Min)',
        type: 'number',
        readOnly: true,
        minWidth: '100px',
        sortType: 'number',
      },
      status: {
        label: 'Status',
        type: 'text',
        readOnly: true,
        filterType: 'select',
        options: statusOptions,
        minWidth: '160px',
      },
      entertainment: {
        label: 'Entertainment',
        type: 'text',
        readOnly: true,
        minWidth: '140px',
        filterType: 'select',
        api: { resource: 'entertainment', valueField: 'entertainmentName', labelField: 'entertainmentName' },
      },
      participantCount: {
        label: 'Personen',
        type: 'number',
        readOnly: true,
        minWidth: '90px',
        filterType: 'number',
        sortType: 'number',
      },
      branchLabel: {
        label: 'Filiale',
        type: 'text',
        readOnly: true,
        minWidth: '140px',
        filterType: 'select',
        api: { resource: 'branch', valueField: '_id', labelField: 'locationName' },
        sortType: 'string',
      },
      creatorLabel: {
        label: 'Ersteller',
        type: 'text',
        readOnly: true,
        minWidth: '130px',
        filterType: 'select',
        options: creatorOptions,
      },
      fullyPaid: {
        label: 'Vollständig bezahlt',
        type: 'boolean',
        readOnly: true,
        minWidth: '120px',
        filterType: 'boolean',
      },
      packageName: { label: 'Paket', type: 'text', readOnly: true, minWidth: '160px' },
      presentSince: { label: 'Anwesend seit', type: 'date', readOnly: true, minWidth: '150px' },
      experienceRating: {
        label: 'Erlebnisbewertung',
        type: 'text',
        readOnly: true,
        minWidth: '130px',
      },
      finishedAt: { label: 'Abgeschlossen am', type: 'date', readOnly: true, minWidth: '150px' },
    }),
    [statusOptions, creatorOptions]
  );

  const listConfig = useMemo(
    () => ({
      endpoint: '/api/v1/internalBooking',
      searchParam: 'searchQuery',
      resultsKey: 'results',
    }),
    []
  );

  const listParams = useMemo(() => ({ selectKeys: BOOKING_SELECT_KEYS.join(',') }), []);

  const transformBookingRow = useCallback(b => {
    if (!b || typeof b !== 'object') return null;
    const durationMin =
      b.eventDurationMS != null && Number.isFinite(Number(b.eventDurationMS))
        ? Math.round(Number(b.eventDurationMS) / 60000)
        : null;
    const branchLabel = b.branchId?.locationName || '';
    const packageName = b.selectedPackage?.name || '';
    const creatorLabel =
      currentUserId && b.meta?.createdBy === currentUserId ? 'Ich' : 'Anderer Nutzer';
    return {
      ...b,
      eventDurationMin: durationMin,
      branchLabel,
      packageName,
      creatorLabel,
      status: statusLabel(b.status),
      experienceRating: experienceLabel(b.experienceRating),
    };
  }, [currentUserId]);

  const dispatchRefresh = useCallback(() => {
    window.dispatchEvent(new CustomEvent('editableListRefresh', { detail: { resource: 'internalBooking' } }));
  }, []);

  return (
    <Box sx={{ ...viewRootTextPrimarySx, p: 2 }}>
      <EditableList
        title='Buchungssuche'
        resourceType='internalBooking'
        hideRowActionsColumn
        schema={bookingSchema}
        visibleColumnKeys={DEFAULT_VISIBLE_KEYS}
        listParams={listParams}
        headerSearchPlaceholder='Suche (Name, Buchungsnummer, Mail, Telefon)'
        initialSearchTerm={initialSearchSeed}
        onRowClick={row => {
          const id = row?._id;
          if (id) setSelectedBookingId(id);
        }}
        options={{
          disableDelete: true,
          disableDuplicate: true,
          hideMongoIdColumn: true,
          disableDetailView: true,
          disableInlineEditing: true,
          listConfig,
          transformItems: transformBookingRow,
          addButtonLabel: 'NEU',
          onCreateClick: () => {
            setSelectedBookingId(null);
            setCreateModalOpen(true);
          },
          fillHeight: true,
          titleKey: 'bookingNumber',
        }}
      />

      <BookingDetailsModal
        open={!!selectedBookingId}
        onClose={() => setSelectedBookingId(null)}
        bookingId={selectedBookingId}
        onUpdate={() => {
          dispatchRefresh();
        }}
      />
      {createModalOpen && (
        <BookingProvider>
          <CreateBookingModal
            open={createModalOpen}
            onClose={() => setCreateModalOpen(false)}
            onSuccess={() => {
              dispatchRefresh();
            }}
            onAfterCreate={({ bookingId }) => {
              if (bookingId) {
                setSelectedBookingId(bookingId);
              }
            }}
          />
        </BookingProvider>
      )}
    </Box>
  );
}

export default BookingSearchView;
